import * as L from "leaflet";
import { LeafletEventHandlerFnMap } from "leaflet";
import { Polygon, useMap } from "react-leaflet";
import { useAsset } from "../useAsset";
import { Annotation } from "../../../model/internal/annotation";
import { BLUE_HIGHLIGHT, DEFAULT_ANNOTATION_COLOR } from "../../../constants/colors";
import { ShapeLineWidth } from "../../../constants/annotation";
import { useDispatch } from "react-redux";
import { updateShapeAnnotationDirectly } from "../../../app/networkSlice";
import ResizeAndTranslateTool from "../../draw-tools/ResizeAndTranslateTool";
import { enforceTrueRectangle } from "../../../utils/shape/enforceTrueRectangle";

interface RectangleAnnotationProps {
  rectangle: Annotation;
  draggable?: boolean;
  highlighted: boolean;
  easyClickTolerance: number;
  arrowSnappingActive: boolean;
  eventHandlers?: LeafletEventHandlerFnMap;
}

const RectangleAnnotation = ({
  rectangle,
  draggable,
  highlighted,
  easyClickTolerance,
  arrowSnappingActive,
  eventHandlers,
}: RectangleAnnotationProps) => {
  const { isAssetSelected, isAssetRingfenced } = useAsset();
  const dispatch = useDispatch();
  const map = useMap();

  const isSelected = isAssetSelected(rectangle.id);
  const isRingfenced = isAssetRingfenced(rectangle.id);

  return (
    <>
      <Polygon
        key={`${rectangle.id}-${draggable}-rectangle-annotation`}
        positions={rectangle.geometry.map((pt: any) => [pt.lat, pt.lng])}
        draggable={false}
        pathOptions={{
          ...rectangle.styles,
          color:
            highlighted || isRingfenced
              ? BLUE_HIGHLIGHT
              : rectangle.styles.color || DEFAULT_ANNOTATION_COLOR,
          weight: ShapeLineWidth,
          fill: highlighted || arrowSnappingActive,
          fillOpacity: 0.1,
          opacity: isRingfenced ? 0.5 : 1,
        }}
        eventHandlers={eventHandlers}
      />
      {!isSelected && (
        <Polygon
          key={`${rectangle.id}-${draggable}-rectangle-selector`}
          positions={rectangle.geometry.map((pt: any) => [pt.lat, pt.lng])}
          draggable={false}
          pathOptions={{
            ...rectangle.styles,
            color: "black",
            weight: ShapeLineWidth + 2 * easyClickTolerance,
            fill: false,
            fillOpacity: 0,
            opacity: 0,
          }}
          eventHandlers={eventHandlers}
        />
      )}
      {isSelected && draggable && (
        <ResizeAndTranslateTool
          id={rectangle.id}
          geometry={rectangle.geometry}
          rotation={true}
          uniformScaling={false}
          onTransform={(newGeometry) => {
            const modifiedGeometry = enforceTrueRectangle(map, newGeometry as L.LatLng[]);
            dispatch(
              updateShapeAnnotationDirectly({
                ...rectangle,
                geometry: modifiedGeometry ?? [],
              }),
            );
          }}
        />
      )}
    </>
  );
};

export default RectangleAnnotation;
